import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  VStack,
  Grid,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useThemeColors } from "@/hooks/useThemeColors";
import { apiClient } from "@/lib/api/client";
import { useToastNotification } from "@/lib/utils/useToastNotification";

/** Today's date as yyyy-mm-dd (local, no conversion). */
function getTodayYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** If date is ISO string, parse and return yyyy-mm-dd in local time; otherwise return as-is. */
function normalizeApiDate(dateRaw: unknown): string {
  if (dateRaw == null) return "";
  if (typeof dateRaw !== "string") return "";
  const s = dateRaw.trim();
  if (!s) return "";
  if (s.includes("T")) {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s.substring(0, 10);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return s.length >= 10 ? s.substring(0, 10) : s;
}

export interface InvoiceFormData {
  invoice_number: string;
  date: string;
  vat: string;
}

export interface InvoiceFormProps {
  /** When provided, form is in edit mode and fetches invoice by ID. */
  invoiceId?: number;
  /** Required for create mode. */
  soaId?: number;
  /** Registered / shipping line name (create: from parent; edit: from fetched invoice). */
  registeredName?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export function InvoiceForm({
  invoiceId,
  soaId,
  registeredName: propRegisteredName = "",
  onSuccess,
  onCancel,
}: InvoiceFormProps) {
  const colors = useThemeColors();
  const { showError, showSuccess } = useToastNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [displayRegisteredName, setDisplayRegisteredName] = useState(propRegisteredName);
  const isMountedRef = useRef(true);

  const isEditMode = !!invoiceId;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<InvoiceFormData>({
    defaultValues: {
      invoice_number: "",
      date: getTodayYmd(),
      vat: "",
    },
  });

  // Fetch invoice when in edit mode and autofill all fields from API
  useEffect(() => {
    if (!isEditMode || !invoiceId) return;
    isMountedRef.current = true;
    const fetchInvoice = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get(`/invoices/${invoiceId}`);
        if (!isMountedRef.current) return;
        const data = response.data?.data ?? response.data ?? {};

        // Invoice number
        const invoiceNumber =
          data.invoice_number ?? (data as Record<string, unknown>).invoice_number ?? "";

        // Registered name: statement_of_account.shipping_line.name (snake_case or camelCase)
        const soa = data.statement_of_account ?? (data as Record<string, unknown>).statement_of_account;
        const shippingLine = soa?.shipping_line ?? (soa as Record<string, unknown>)?.shippingLine;
        const registeredName =
          shippingLine?.name ?? (shippingLine as Record<string, unknown>)?.name ?? "";

        // Date: if ISO string, parse and show local date; otherwise display as-is
        const dateRaw = data.date ?? (data as Record<string, unknown>).date;
        const dateStr = normalizeApiDate(dateRaw);

        // Discount
        const discount = data.discount ?? (data as Record<string, unknown>).discount ?? 0;
        const vatStr =
          discount === 0 || discount === null || discount === undefined
            ? ""
            : String(discount);

        setDisplayRegisteredName(String(registeredName));
        reset({
          invoice_number: String(invoiceNumber),
          date: dateStr,
          vat: vatStr,
        });
      } catch (error: unknown) {
        if (isMountedRef.current) showError(error);
      } finally {
        if (isMountedRef.current) setIsLoading(false);
      }
    };

    fetchInvoice();
    return () => {
      isMountedRef.current = false;
    };
  }, [isEditMode, invoiceId, reset, showError]);

  const onSubmit = async (data: InvoiceFormData) => {
    const discount = data.vat === "" ? 0 : Number(data.vat);
    if (Number.isNaN(discount) || discount < 0) {
      showError("Discount must be a number >= 0");
      return;
    }

    // Send date as yyyy-mm-dd only (trim to avoid passing spaces)
    const rawDate = (data.date ?? "").trim();
    const dateYmd = rawDate ? rawDate.substring(0, 10) : rawDate;

    setIsSubmitting(true);
    try {
      if (isEditMode && invoiceId) {
        await apiClient.put(`/invoices/${invoiceId}`, {
          invoice_number: data.invoice_number.trim(),
          date: dateYmd,
          discount,
        });
        showSuccess("Success", "Invoice updated successfully");
      } else if (soaId != null) {
        await apiClient.post("/invoices/generate", {
          statement_of_account_id: soaId,
          invoice_number: data.invoice_number.trim(),
          date: dateYmd,
          discount,
        });
        showSuccess("Success", "Invoice created successfully");
      } else {
        showError("SOA ID is required to create an invoice");
        return;
      }
      onSuccess();
    } catch (error: unknown) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={4} color={colors.text}>
          Loading invoice...
        </Text>
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      <Box as="form" onSubmit={handleSubmit(onSubmit)}>
        <VStack spacing={4} align="stretch">
              {/* Registered Name (read-only) */}
              <Grid
                templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                gap={4}
                alignItems="start"
              >
                <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                  Registered Name: <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <FormControl>
                  <Input
                    value={displayRegisteredName}
                    readOnly
                    bg={colors.sectionBg}
                    borderColor={colors.border}
                    color={colors.text}
                  />
                </FormControl>
              </Grid>

              {/* Invoice Number: editable in create and edit */}
              <Grid
                templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                gap={4}
                alignItems="start"
              >
                <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                  Invoice Number: <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <FormControl isInvalid={!!errors.invoice_number}>
                  <Input
                    {...register("invoice_number", {
                      required: "Invoice number is required",
                      validate: (v) =>
                        (typeof v === "string" && v.trim().length > 0) ||
                        "Invoice number is required",
                    })}
                    placeholder="Input invoice number"
                    borderColor={colors.border}
                    color={colors.text}
                  />
                  <FormErrorMessage>{errors.invoice_number?.message}</FormErrorMessage>
                </FormControl>
              </Grid>

              {/* Date (calendar picker) */}
              <Grid
                templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                gap={4}
                alignItems="start"
              >
                <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                  Date: <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <FormControl isInvalid={!!errors.date}>
                  <Input
                    type="date"
                    {...register("date", { required: "Date is required" })}
                    borderColor={colors.border}
                    color={colors.text}
                  />
                  <FormErrorMessage>{errors.date?.message}</FormErrorMessage>
                </FormControl>
              </Grid>

              {/* Discount */}
              <Grid
                templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                gap={4}
                alignItems="start"
              >
                <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                  Discount:
                </FormLabel>
                <FormControl isInvalid={!!errors.vat}>
                  <Input
                    type="number"
                    min={0}
                    placeholder="0.00"
                    {...register("vat", {
                      setValueAs: (v) => (v === "" ? "" : v),
                      validate: (v) => {
                        if (v === "") return true;
                        const n = Number(v);
                        if (Number.isNaN(n)) return "Must be a number";
                        if (n < 0) return "Must be 0 or greater";
                        return true;
                      },
                    })}
                    borderColor={colors.border}
                    color={colors.text}
                  />
                  <FormErrorMessage>{errors.vat?.message}</FormErrorMessage>
                </FormControl>
              </Grid>

              {/* Action Buttons */}
              <HStack spacing={4} justify="flex-end" pt={2}>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  borderColor={colors.border}
                  color={colors.text}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="blue"
                  isLoading={isSubmitting}
                  loadingText={isEditMode ? "Updating..." : "Creating..."}
                >
                  {isEditMode ? "Update" : "Create"}
                </Button>
              </HStack>
        </VStack>
      </Box>
    </VStack>
  );
}
